import { theme } from 'antd'
import { createStyles } from 'antd-style'
import { createElement } from 'react'

import { SkalxAiIcon } from 'src/components'

import { agentGlyphSource, lucideIconByName } from '../agentGlyph'
import type { AgentGlyphAgent } from '../agentSnapshot'

const GLYPH_RATIO = 0.65
const RADIUS_RATIO = 0.25

const useStyles = createStyles(({ token }) => ({
  plate: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    color: token.colorTextLightSolid,
    background: token.colorPrimary
  },

  photoPlate: {
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorderSecondary}`,
    overflow: 'hidden'
  },

  photo: {
    width: '100%',
    height: '100%',
    padding: token.paddingXXS,
    objectFit: 'contain'
  }
}))

type Props = {
  agent: AgentGlyphAgent | undefined
  /** Plate box in px. Defaults to the theme's large control height. */
  size?: number
  className?: string
}

/**
 * The workspace agent's face wherever the chat speaks as it. An agent with
 * neither a photo nor a picked glyph falls back to the Skalx brand mark, which
 * carries its own plate and so stands in for this one rather than nesting.
 */
export const AgentGlyph = ({ agent, size, className }: Props) => {
  const { styles, cx } = useStyles()
  const { token } = theme.useToken()
  const source = agentGlyphSource(agent)
  const plateSize = size ?? token.controlHeightLG

  if (!source) {
    return <SkalxAiIcon size={plateSize} className={className} />
  }

  const plate = {
    width: plateSize,
    height: plateSize,
    borderRadius: Math.round(plateSize * RADIUS_RATIO)
  }

  if (source.kind === 'photo') {
    return (
      <span
        className={cx(styles.plate, styles.photoPlate, className)}
        style={plate}
      >
        <img src={source.url} alt="" className={styles.photo} />
      </span>
    )
  }

  const Icon = lucideIconByName(source.name)

  return (
    <span
      className={cx(styles.plate, className)}
      style={source.color ? { ...plate, background: source.color } : plate}
    >
      {Icon
        ? createElement(Icon, { size: Math.round(plateSize * GLYPH_RATIO) })
        : null}
    </span>
  )
}
